import { motion, type Transition } from 'framer-motion'
import { EASE_OUT, LOOP } from '../tokens/motion'

/**
 * The Workflow mark: five rounded tiles forming a forward chevron.
 *
 * It shares Project Studio's tile language (Studio's tiles descend in a
 * staircase) and turns it into a direction: many inputs gathered and sent
 * on, which is what the platform does. Drawn in `currentColor`.
 *
 * `busy` ripples the tiles from the top arm through the tip to the bottom
 * arm, in the order the chevron is read. `entrance` assembles it once.
 */

const TILE = { size: 22, radius: 5 } as const
const STEP = 25

/** Column and row of each tile, in reading order. */
const TILES = [
  [0, 0],
  [1, 1],
  [2, 2],
  [1, 3],
  [0, 4],
] as const

function tileMotion(index: number, busy: boolean, entrance: boolean) {
  if (entrance) {
    return {
      initial: { opacity: 0, scale: 0.82 },
      animate: { opacity: 1, scale: 1 },
      transition: { duration: 0.44, delay: 0.08 + index * 0.1, ease: EASE_OUT } as Transition,
    }
  }
  if (busy) {
    return {
      initial: { opacity: 1, scale: 1 },
      animate: { opacity: [0.55, 1, 0.55], scale: [0.92, 1, 0.92] },
      transition: { ...LOOP.pulse, delay: index * (LOOP.pulse.duration / 8) } as Transition,
    }
  }
  return {
    initial: false as const,
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.36, ease: EASE_OUT } as Transition,
  }
}

export function WorkflowMark({
  size = 16,
  busy = false,
  entrance = false,
  className,
  title,
}: {
  /** Rendered height in px. The mark keeps its 5:8 ratio. */
  size?: number
  busy?: boolean
  entrance?: boolean
  className?: string
  /** Set when the mark stands alone and needs an accessible name. */
  title?: string
}) {
  return (
    <svg
      height={size}
      width={(size * 80) / 128}
      viewBox="0 0 80 128"
      fill="none"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      className={className}
    >
      {TILES.map(([col, row], index) => {
        const props = tileMotion(index, busy, entrance)
        return (
          <motion.rect
            key={`${col}-${row}`}
            x={4 + col * STEP}
            y={3 + row * STEP}
            width={TILE.size}
            height={TILE.size}
            rx={TILE.radius}
            fill="currentColor"
            style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
            initial={props.initial}
            animate={props.animate}
            transition={props.transition}
          />
        )
      })}
    </svg>
  )
}
